import React from "react";

import { FieldPath, FieldValues } from "react-hook-form";
import { NumericFormat, NumericFormatProps } from "react-number-format";
import TextField, { TextFieldProps } from "@mui/material/TextField";

import ControllerInput, { ControllerInputProps } from "@/components/atoms/Form/ControllerInput/ControllerInput";

export const euroProps: Pick<NumericFormatProps, "thousandSeparator" | "decimalSeparator" | "decimalScale" | "suffix"> =
    {
        thousandSeparator: " ",
        decimalSeparator: ",",
        decimalScale: 2,
        suffix: " €",
    };

export type EuroInputProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<ControllerInputProps<TFieldValues, TName>, "render"> & Omit<NumericFormatProps<TextFieldProps>, "form">;

const EuroInput = <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
    name,
    control,
    defaultValue = null,
    FormControlProps,
    LabelProps,
    hideErrorMessage,
    label,
    fullWidth,
    required = false,
    ...phoneFieldProps
}: EuroInputProps<TFieldValues, TName>): React.ReactElement => {
    return (
        <ControllerInput
            control={control}
            name={name}
            defaultValue={defaultValue}
            label={label}
            LabelProps={LabelProps}
            FormControlProps={FormControlProps}
            required={required}
            fullWidth={fullWidth}
            hideErrorMessage={hideErrorMessage}
            render={({ field: { onChange, onBlur, value, ref }, error }) => (
                <NumericFormat
                    id={name}
                    name={name}
                    value={value ?? ""}
                    customInput={TextField}
                    onChange={onChange}
                    onBlur={onBlur}
                    fullWidth={fullWidth}
                    inputRef={ref}
                    error={!!error}
                    InputProps={{
                        inputProps: {
                            name,
                            "data-testid": `euro_input-${name}`,
                        },
                    }}
                    {...euroProps}
                    {...phoneFieldProps}
                />
            )}
        />
    );
};

export default EuroInput;
